import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';

// Informations légales de l'entreprise
const COMPANY = {
  name: "Axeris Labs",
  legalName: "Valentin Bechameil",
  status: "Auto-entrepreneur",
  siret: "947 597 696",
  address: "61 rue de Lyon",
  city: "75012 Paris",
  email: "contact@axeris-labs.fr",
  website: "axeris-labs.fr"
};

export function MentionsLegales() {
  return (
    <main data-testid="mentions-legales-page" className="pt-32 pb-20">
      <SEO 
        title="Mentions légales"
        description="Mentions légales du site Axeris Labs - Informations sur l'éditeur, l'hébergeur et les conditions d'utilisation du site."
        path="/mentions-legales"
      />
      <div className="container-main max-w-4xl">
        <Link to="/">
          <Button variant="ghost" className="mb-8">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour à l'accueil
          </Button>
        </Link>

        <h1 className="text-4xl md:text-5xl font-bold mb-8">Mentions légales</h1>

        <div className="prose prose-lg dark:prose-invert max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-bold mb-4">1. Éditeur du site</h2>
            <p className="text-muted-foreground">
              <strong>Nom :</strong> {COMPANY.legalName}<br />
              <strong>Nom commercial :</strong> {COMPANY.name}<br />
              <strong>Statut :</strong> {COMPANY.status}<br />
              <strong>SIRET :</strong> {COMPANY.siret}<br />
              <strong>Adresse :</strong> {COMPANY.address}, {COMPANY.city}<br />
              <strong>Email :</strong> <a href={`mailto:${COMPANY.email}`} className="text-primary hover:underline">{COMPANY.email}</a><br />
              <strong>Enregistrement :</strong> Greffe de Paris<br /><br />
              En tant qu'auto-entrepreneur bénéficiant de la franchise en base de TVA (article 293 B du CGI), aucune TVA n'est applicable.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">2. Directeur de la publication</h2>
            <p className="text-muted-foreground">
              {COMPANY.legalName}, en qualité d'éditeur du site {COMPANY.website}.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">3. Hébergement</h2>
            <p className="text-muted-foreground">
              <strong>Hébergeur :</strong> Hetzner Online GmbH<br />
              <strong>Adresse :</strong> Industriestr. 25, 91710 Gunzenhausen, Allemagne<br />
              <strong>Site web :</strong> <a href="https://www.hetzner.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">https://www.hetzner.com</a>
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">4. Propriété intellectuelle</h2>
            <p className="text-muted-foreground">
              L'ensemble du contenu du site {COMPANY.website} (textes, images, logos, design, code) est la propriété exclusive de {COMPANY.legalName}, sauf mention contraire. Toute reproduction, représentation, modification ou exploitation, totale ou partielle, sans autorisation préalable et écrite est strictement interdite.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">5. Données personnelles</h2>
            <p className="text-muted-foreground">
              Les données personnelles collectées sur ce site (formulaire de contact, espace client) sont traitées conformément au Règlement Général sur la Protection des Données (RGPD – Règlement UE 2016/679).<br /><br />
              <strong>Responsable du traitement :</strong> {COMPANY.legalName} – <a href={`mailto:${COMPANY.email}`} className="text-primary hover:underline">{COMPANY.email}</a><br /><br />
              Les données collectées sont utilisées uniquement dans le cadre de la relation commerciale et ne sont jamais vendues ni transmises à des tiers, à l'exception des prestataires techniques nécessaires au fonctionnement du site (Stripe pour les paiements, Hetzner pour l'hébergement).<br /><br />
              Conformément à la loi Informatique et Libertés du 6 janvier 1978 et au RGPD, vous disposez d'un droit d'accès, de rectification, de suppression et de portabilité de vos données. Pour exercer ces droits, contactez : <a href={`mailto:${COMPANY.email}`} className="text-primary hover:underline">{COMPANY.email}</a>
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">6. Cookies</h2>
            <p className="text-muted-foreground">
              Le site {COMPANY.website} peut utiliser des cookies techniques nécessaires au bon fonctionnement du site (session utilisateur, préférences d'affichage). Aucun cookie publicitaire ou de tracking tiers n'est utilisé sans consentement préalable.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">7. Liens hypertextes</h2>
            <p className="text-muted-foreground">
              Le site {COMPANY.website} peut contenir des liens vers des sites tiers. {COMPANY.name} ne peut être tenu responsable du contenu de ces sites externes et de leur politique de confidentialité.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">8. Droit applicable</h2>
            <p className="text-muted-foreground">
              Les présentes mentions légales sont soumises au droit français. Tout litige relatif à l'utilisation du site {COMPANY.website} sera soumis aux juridictions françaises compétentes.
            </p>
          </section>

          <p className="text-muted-foreground text-sm mt-8">
            <em>Dernière mise à jour : 6 mars 2026</em>
          </p>
        </div>
      </div>
    </main>
  );
}

export function PolitiqueConfidentialite() {
  return (
    <main data-testid="politique-confidentialite-page" className="pt-32 pb-20">
      <SEO 
        title="Politique de confidentialité"
        description="Politique de confidentialité d'Axeris Labs - Comment vos données personnelles sont collectées, utilisées et protégées conformément au RGPD."
        path="/politique-confidentialite"
      />
      <div className="container-main max-w-4xl">
        <Link to="/">
          <Button variant="ghost" className="mb-8">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour à l'accueil
          </Button>
        </Link>

        <h1 className="text-4xl md:text-5xl font-bold mb-8">Politique de confidentialité</h1>

        <div className="prose prose-lg dark:prose-invert max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-bold mb-4">1. Responsable du traitement</h2>
            <p className="text-muted-foreground">
              <strong>Nom :</strong> {COMPANY.legalName}<br />
              <strong>Nom commercial :</strong> {COMPANY.name}<br />
              <strong>SIRET :</strong> {COMPANY.siret}<br />
              <strong>Adresse :</strong> {COMPANY.address}, {COMPANY.city}<br />
              <strong>Email :</strong> <a href={`mailto:${COMPANY.email}`} className="text-primary hover:underline">{COMPANY.email}</a>
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">2. Données collectées</h2>
            
            <h3 className="text-xl font-semibold mb-2">2.1 Espace client</h3>
            <p className="text-muted-foreground mb-4">
              Lors de la création d'un compte ou d'une commande, les données suivantes sont collectées :<br /><br />
              • Nom et prénom<br />
              • Adresse email<br />
              • Informations relatives au projet (nature, budget, besoins)<br /><br />
              Ces données sont nécessaires à la gestion de la relation commerciale, à l'exécution des prestations et à la facturation.
            </p>

            <h3 className="text-xl font-semibold mb-2">2.2 Paiements via Stripe</h3>
            <p className="text-muted-foreground mb-4">
              Les paiements sont traités par Stripe, Inc. {COMPANY.name} ne collecte ni ne stocke aucune donnée bancaire. Les informations de paiement (numéro de carte, coordonnées bancaires) sont transmises directement à Stripe et traitées selon leur politique de confidentialité, disponible sur <a href="https://stripe.com/fr/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">https://stripe.com/fr/privacy</a>.<br /><br />
              Stripe est certifié PCI-DSS niveau 1, le plus haut niveau de certification en matière de sécurité des paiements.
            </p>

            <h3 className="text-xl font-semibold mb-2">2.3 Google Analytics</h3>
            <p className="text-muted-foreground">
              Le site utilise Google Analytics pour mesurer l'audience et améliorer l'expérience utilisateur. Google Analytics collecte de manière anonymisée les données suivantes :<br /><br />
              • Pages visitées et durée de visite<br />
              • Provenance du trafic (moteur de recherche, lien direct, réseaux sociaux...)<br />
              • Type d'appareil et navigateur utilisé<br />
              • Localisation approximative (pays, région)<br /><br />
              Ces données sont collectées via des cookies et transmises à Google LLC, dont les serveurs sont situés aux États-Unis. Ce transfert est encadré par les clauses contractuelles types de la Commission européenne.<br /><br />
              Vous pouvez désactiver Google Analytics en installant l'extension officielle disponible sur : <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">https://tools.google.com/dlpage/gaoptout</a>
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">3. Finalités du traitement</h2>
            <p className="text-muted-foreground">
              Les données collectées sont utilisées pour les finalités suivantes :<br /><br />
              • Gestion des commandes et de la relation client<br />
              • Exécution des prestations de création de sites web et d'applications<br />
              • Facturation et suivi comptable<br />
              • Amélioration du site et de l'expérience utilisateur (Google Analytics)<br />
              • Communication relative aux projets en cours<br /><br />
              Aucune donnée n'est utilisée à des fins publicitaires ni revendue à des tiers.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">4. Base légale du traitement</h2>
            <p className="text-muted-foreground">
              • <strong>Exécution d'un contrat :</strong> traitement des commandes et des prestations<br />
              • <strong>Intérêt légitime :</strong> amélioration du site via Google Analytics<br />
              • <strong>Consentement :</strong> cookies non essentiels (vous pouvez retirer votre consentement à tout moment)
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">5. Durée de conservation</h2>
            <p className="text-muted-foreground">
              • <strong>Données client :</strong> conservées pendant la durée de la relation commerciale, puis 5 ans à compter de la dernière commande (obligation légale comptable)<br />
              • <strong>Données de navigation (Google Analytics) :</strong> 26 mois maximum<br />
              • <strong>Données de paiement :</strong> gérées par Stripe selon leur propre politique de rétention
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">6. Sous-traitants et destinataires</h2>
            <p className="text-muted-foreground">
              Les données peuvent être transmises aux sous-traitants suivants, dans le strict cadre de leur mission :<br /><br />
              • <strong>Stripe, Inc.</strong> (traitement des paiements) – <a href="https://stripe.com/fr/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">https://stripe.com/fr/privacy</a><br />
              • <strong>Google LLC</strong> (Google Analytics) – <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">https://policies.google.com/privacy</a><br />
              • <strong>Hetzner Online GmbH</strong> (hébergement) – <a href="https://www.hetzner.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">https://www.hetzner.com/legal/privacy-policy</a><br /><br />
              Aucune donnée n'est transmise à d'autres tiers sans votre consentement explicite.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">7. Vos droits</h2>
            <p className="text-muted-foreground">
              Conformément au RGPD et à la loi Informatique et Libertés, vous disposez des droits suivants sur vos données personnelles :<br /><br />
              • <strong>Droit d'accès :</strong> obtenir une copie de vos données<br />
              • <strong>Droit de rectification :</strong> corriger des données inexactes<br />
              • <strong>Droit à l'effacement :</strong> demander la suppression de vos données<br />
              • <strong>Droit à la portabilité :</strong> recevoir vos données dans un format structuré<br />
              • <strong>Droit d'opposition :</strong> vous opposer à certains traitements<br />
              • <strong>Droit de limitation :</strong> restreindre l'utilisation de vos données<br /><br />
              Pour exercer ces droits, contactez : <a href={`mailto:${COMPANY.email}`} className="text-primary hover:underline">{COMPANY.email}</a>. Une réponse sera apportée dans un délai maximum de 30 jours.<br /><br />
              Si vous estimez que vos droits ne sont pas respectés, vous pouvez introduire une réclamation auprès de la CNIL (<a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">www.cnil.fr</a>).
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">8. Cookies</h2>
            
            <h3 className="text-xl font-semibold mb-2">8.1 Cookies techniques (essentiels)</h3>
            <p className="text-muted-foreground mb-4">
              Ces cookies sont nécessaires au fonctionnement du site (session utilisateur, préférences, sécurité). Ils ne peuvent pas être désactivés.
            </p>

            <h3 className="text-xl font-semibold mb-2">8.2 Cookies analytiques (Google Analytics)</h3>
            <p className="text-muted-foreground">
              Ces cookies permettent de mesurer l'audience du site. Ils sont déposés uniquement après votre consentement. Vous pouvez retirer ce consentement à tout moment via le bandeau cookies ou en installant l'extension de désactivation Google Analytics.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">9. Sécurité</h2>
            <p className="text-muted-foreground">
              {COMPANY.name} met en œuvre les mesures techniques et organisationnelles appropriées pour protéger vos données contre tout accès non autorisé, perte ou divulgation. Le site est servi en HTTPS, les mots de passe sont stockés de manière chiffrée et les accès à l'infrastructure sont sécurisés.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">10. Modifications</h2>
            <p className="text-muted-foreground">
              La présente politique de confidentialité peut être mise à jour à tout moment. La date de dernière mise à jour est indiquée en bas de page. En cas de modification substantielle, les utilisateurs enregistrés seront informés par email.
            </p>
          </section>

          <p className="text-muted-foreground text-sm mt-8">
            <em>Dernière mise à jour : 6 mars 2026</em>
          </p>
        </div>
      </div>
    </main>
  );
}

export function CGV() {
  return (
    <main data-testid="cgv-page" className="pt-32 pb-20">
      <SEO 
        title="Conditions Générales de Vente"
        description="CGV d'Axeris Labs - Conditions générales de vente pour les prestations de création de sites web, hébergement et maintenance."
        path="/cgv"
      />
      <div className="container-main max-w-4xl">
        <Link to="/">
          <Button variant="ghost" className="mb-8">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour à l'accueil
          </Button>
        </Link>

        <h1 className="text-4xl md:text-5xl font-bold mb-8">Conditions Générales de Vente</h1>

        <div className="prose prose-lg dark:prose-invert max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-bold mb-4">Article 1 – Identification du prestataire</h2>
            <p className="text-muted-foreground">
              {COMPANY.legalName}, auto-entrepreneur immatriculé sous le numéro SIRET {COMPANY.siret} au greffe de Paris ({COMPANY.address}, {COMPANY.city}), exerçant sous le nom commercial {COMPANY.name}.<br /><br />
              Contact : <a href={`mailto:${COMPANY.email}`} className="text-primary hover:underline">{COMPANY.email}</a>
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">Article 2 – Objet</h2>
            <p className="text-muted-foreground">
              Les présentes Conditions Générales de Vente (CGV) régissent l'ensemble des prestations de services proposées par {COMPANY.name}, notamment :<br /><br />
              • Création de sites web (vitrine, landing page, e-commerce)<br />
              • Développement d'applications web sur mesure<br />
              • Hébergement et maintenance de sites web<br /><br />
              Elles s'appliquent à toute commande passée auprès d'{COMPANY.name}, sauf dérogation expresse et écrite.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">Article 3 – Devis et commande</h2>
            <p className="text-muted-foreground">
              Toute prestation fait l'objet d'un devis préalable, transmis au client par email. Le devis est valable 30 jours à compter de sa date d'émission.<br /><br />
              La commande est réputée ferme et définitive dès le versement de l'acompte prévu à l'article 5, effectué via l'espace client sur le site {COMPANY.website}. Le client valide sa commande en cliquant sur le bouton de paiement dans son espace personnel, ce qui vaut acceptation des présentes CGV.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">Article 4 – Tarifs</h2>
            <p className="text-muted-foreground">
              Les tarifs sont indiqués en euros hors taxes (HT). En tant qu'auto-entrepreneur bénéficiant de la franchise en base de TVA (article 293 B du CGI), aucune TVA n'est applicable. La mention "TVA non applicable" figure sur les factures.<br /><br />
              Les prix affichés sur le site {COMPANY.website} sont des fourchettes indicatives. Le tarif final est déterminé après échange avec le client en fonction de la complexité du projet.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">Article 5 – Modalités de paiement</h2>
            
            <h3 className="text-xl font-semibold mb-2">5.1 – Acompte et solde</h3>
            <p className="text-muted-foreground mb-4">
              Sur le site, les modalités de règlement disponibles par défaut depuis l'espace client sont les suivantes :<br /><br />
              • <strong>Paiement échelonné en interne (30 / 70)</strong> : 30 % du montant total à la commande (acompte), puis 70 % à la livraison du projet.<br />
              • <strong>Paiement intégral en 3 fois sans frais via Klarna</strong> : l'intégralité du montant est réglée dès la commande, le fractionnement étant géré directement par Klarna avec le client.<br /><br />
              Dans certains cas (projets institutionnels, sites vitrines avancés, applications web, prestations sur mesure de plus grande ampleur, etc.), d'autres modalités de règlement pourront être convenues d'un commun accord entre le client et {COMPANY.name}. Ces modalités spécifiques sont alors précisées dans le devis correspondant et prévalent sur celles décrites ci‑dessus.
            </p>

            <h3 className="text-xl font-semibold mb-2">5.2 – Moyens de paiement acceptés</h3>
            <p className="text-muted-foreground mb-4">
              L'ensemble des paiements est traité de manière sécurisée via Stripe. Les moyens de paiement disponibles sont les suivants :<br /><br />
              • Carte bancaire (Visa, Mastercard, American Express)<br />
              • Virement bancaire<br />
              • Prélèvement SEPA<br />
              • Paiement en 3 fois sans frais via Klarna<br />
              • Espèces (sur accord préalable uniquement)<br /><br />
              {COMPANY.name} ne stocke à aucun moment les données bancaires du client. Ces données sont traitées exclusivement par Stripe, conformément à sa politique de sécurité (PCI-DSS).
            </p>

            <h3 className="text-xl font-semibold mb-2">5.3 – Retard de paiement</h3>
            <p className="text-muted-foreground">
              Tout retard de paiement entraîne l'application de pénalités égales à 3 fois le taux d'intérêt légal en vigueur, conformément à l'article L. 441-10 du Code de commerce. Une indemnité forfaitaire de 40 € pour frais de recouvrement sera également due.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">Article 6 – Annulation et remboursement</h2>
            <p className="text-muted-foreground">
              Les prestations de création de sites web et d'applications sont des prestations sur mesure, exécutées à la demande du client. À ce titre, conformément à l'article L. 221-28 du Code de la consommation, elles sont exclues du droit de rétractation.<br /><br />
              En cas d'annulation par le client après versement de l'acompte et démarrage des travaux, l'acompte reste définitivement acquis à {COMPANY.name} à titre de dédommagement. Si des travaux supplémentaires ont été réalisés au-delà de l'acompte, une facturation complémentaire pourra être émise au prorata de l'avancement.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">Article 7 – Délais de livraison</h2>
            <p className="text-muted-foreground">
              Les délais de livraison sont indiqués à titre indicatif dans le devis. Ils courent à compter de la réception de l'acompte et de la transmission par le client de l'ensemble des éléments nécessaires à la réalisation du projet (textes, images, accès, etc.).<br /><br />
              {COMPANY.name} ne saurait être tenu responsable des retards occasionnés par un manque de réactivité du client dans la fourniture des éléments requis.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">Article 8 – Obligations du client</h2>
            <p className="text-muted-foreground">
              Le client s'engage à :<br /><br />
              • Fournir des informations exactes et complètes nécessaires à la réalisation du projet<br />
              • Transmettre dans les délais convenus les contenus (textes, images, logos, etc.)<br />
              • Disposer des droits nécessaires sur les éléments transmis à {COMPANY.name}<br />
              • Valider les étapes du projet dans des délais raisonnables<br /><br />
              Le client est seul responsable des contenus publiés sur son site. {COMPANY.name} ne saurait être tenu responsable de contenus illicites ou portant atteinte aux droits de tiers.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">Article 9 – Propriété intellectuelle</h2>
            
            <h3 className="text-xl font-semibold mb-2">9.1 – Transfert de droits</h3>
            <p className="text-muted-foreground mb-4">
              À compter du paiement intégral de la prestation, le client acquiert la pleine propriété du site livré ainsi que les droits d'exploitation sur les créations réalisées spécifiquement pour son projet.
            </p>

            <h3 className="text-xl font-semibold mb-2">9.2 – Référence commerciale</h3>
            <p className="text-muted-foreground">
              {COMPANY.name} se réserve le droit de mentionner la réalisation du projet dans son portfolio et à des fins de prospection commerciale, sauf opposition expresse du client.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">Article 10 – Hébergement et maintenance</h2>
            <p className="text-muted-foreground">
              Les prestations d'hébergement décrites sur {COMPANY.website} (formules à 15 € et 17,50 € par mois) concernent exclusivement l'hébergement et la maintenance des <strong>sites vitrines</strong>. L'abonnement est mensuel, reconductible tacitement chaque mois. Le client peut résilier son abonnement à tout moment depuis son espace client sur le site {COMPANY.website} via le bouton de résiliation prévu à cet effet, ou par email à <a href={`mailto:${COMPANY.email}`} className="text-primary hover:underline">{COMPANY.email}</a> avec un préavis de 30 jours.<br /><br />
              Pour les <strong>projets institutionnels, certains sites vitrines avancés et les applications web</strong>, le tarif et le périmètre des prestations d'hébergement et de maintenance peuvent être différents (infrastructure dédiée, supervision, sauvegardes étendues, SLA spécifiques, etc.) et sont définis d'un commun accord entre le client et {COMPANY.name} dans le devis correspondant.<br /><br />
              En cas de résiliation, les fichiers du site sont restitués au client sur demande dans un délai de 30 jours.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">Article 11 – Responsabilité</h2>
            <p className="text-muted-foreground">
              {COMPANY.name} s'engage à apporter tout le soin nécessaire à la réalisation des prestations. Sa responsabilité est limitée au montant total de la prestation facturée. {COMPANY.name} ne saurait être tenu responsable des dommages indirects, pertes de chiffre d'affaires ou manques à gagner.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">Article 12 – Confidentialité et données personnelles</h2>
            <p className="text-muted-foreground">
              {COMPANY.name} s'engage à traiter de manière confidentielle toutes les informations communiquées par le client dans le cadre de la prestation. Les données personnelles collectées sont utilisées uniquement pour la gestion des commandes et la relation commerciale, conformément au Règlement Général sur la Protection des Données (RGPD).
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">Article 13 – Règlement des litiges</h2>
            <p className="text-muted-foreground">
              En cas de litige entre les parties, une solution amiable sera recherchée en priorité. À défaut d'accord, et pour les contrats conclus avec des professionnels (B2B), le Tribunal de Commerce de Bourg-en-Bresse sera seul compétent, quelle que soit la nationalité des parties.<br /><br />
              Pour les contrats conclus avec des particuliers (B2C), la juridiction compétente sera celle du domicile du consommateur, conformément aux dispositions du Code de la consommation.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">Article 14 – Droit applicable</h2>
            <p className="text-muted-foreground">
              Les présentes CGV sont soumises au droit français.
            </p>
          </section>

          <p className="text-muted-foreground text-sm mt-8">
            <em>Version en vigueur au 6 mars 2026</em>
          </p>
        </div>
      </div>
    </main>
  );
}
